import axios from 'axios';
import crud from './helpers/crud';
import redirectToLogin from './helpers/redirectToLogin';
import { csrfToken } from '../utils/csrfHeaders';

// Action Types
//
// The one-slice rule: every Patient Engagement data interaction is an action
// creator in this file, going through crud(). No component imports axios.
// New resources (automation rules — PR 4, broadcasts — PR 5, conversations —
// PR 6) extend the PE_* constants here and keys in reducers/patientEngagement.
export const PE_SETTINGS = {
  FETCH_BEGIN: 'PE_SETTINGS_FETCH_BEGIN',
  FETCH_SUCCESS: 'PE_SETTINGS_FETCH_SUCCESS',
  FETCH_FAIL: 'PE_SETTINGS_FETCH_FAIL',
  UPDATE_BEGIN: 'PE_SETTINGS_UPDATE_BEGIN',
  UPDATE_SUCCESS: 'PE_SETTINGS_UPDATE_SUCCESS',
  UPDATE_FAIL: 'PE_SETTINGS_UPDATE_FAIL',
};

export const PE_AUTOMATION_RULES = {
  ARCHIVED_BEGIN: 'PE_AUTOMATION_RULES_ARCHIVED_BEGIN',
  ARCHIVED_SUCCESS: 'PE_AUTOMATION_RULES_ARCHIVED_SUCCESS',
  ARCHIVED_FAIL: 'PE_AUTOMATION_RULES_ARCHIVED_FAIL',
  RESTORE_SUCCESS: 'PE_AUTOMATION_RULES_RESTORE_SUCCESS',
  FETCH_BEGIN: 'PE_AUTOMATION_RULES_FETCH_BEGIN',
  FETCH_SUCCESS: 'PE_AUTOMATION_RULES_FETCH_SUCCESS',
  FETCH_FAIL: 'PE_AUTOMATION_RULES_FETCH_FAIL',
  CREATE_BEGIN: 'PE_AUTOMATION_RULES_CREATE_BEGIN',
  CREATE_SUCCESS: 'PE_AUTOMATION_RULES_CREATE_SUCCESS',
  CREATE_FAIL: 'PE_AUTOMATION_RULES_CREATE_FAIL',
  UPDATE_BEGIN: 'PE_AUTOMATION_RULES_UPDATE_BEGIN',
  UPDATE_SUCCESS: 'PE_AUTOMATION_RULES_UPDATE_SUCCESS',
  UPDATE_FAIL: 'PE_AUTOMATION_RULES_UPDATE_FAIL',
  DELETE_BEGIN: 'PE_AUTOMATION_RULES_DELETE_BEGIN',
  DELETE_SUCCESS: 'PE_AUTOMATION_RULES_DELETE_SUCCESS',
  DELETE_FAIL: 'PE_AUTOMATION_RULES_DELETE_FAIL',
  REORDER_BEGIN: 'PE_AUTOMATION_RULES_REORDER_BEGIN',
  REORDER_SUCCESS: 'PE_AUTOMATION_RULES_REORDER_SUCCESS',
  REORDER_FAIL: 'PE_AUTOMATION_RULES_REORDER_FAIL',
  PREVIEW_BEGIN: 'PE_AUTOMATION_RULES_PREVIEW_BEGIN',
  PREVIEW_SUCCESS: 'PE_AUTOMATION_RULES_PREVIEW_SUCCESS',
  PREVIEW_FAIL: 'PE_AUTOMATION_RULES_PREVIEW_FAIL',
};

export const PE_BROADCASTS = {
  FETCH_BEGIN: 'PE_BROADCASTS_FETCH_BEGIN',
  FETCH_SUCCESS: 'PE_BROADCASTS_FETCH_SUCCESS',
  FETCH_FAIL: 'PE_BROADCASTS_FETCH_FAIL',
  FETCH_ONE_BEGIN: 'PE_BROADCASTS_FETCH_ONE_BEGIN',
  FETCH_ONE_SUCCESS: 'PE_BROADCASTS_FETCH_ONE_SUCCESS',
  FETCH_ONE_FAIL: 'PE_BROADCASTS_FETCH_ONE_FAIL',
  CREATE_BEGIN: 'PE_BROADCASTS_CREATE_BEGIN',
  CREATE_SUCCESS: 'PE_BROADCASTS_CREATE_SUCCESS',
  CREATE_FAIL: 'PE_BROADCASTS_CREATE_FAIL',
  UPDATE_BEGIN: 'PE_BROADCASTS_UPDATE_BEGIN',
  UPDATE_SUCCESS: 'PE_BROADCASTS_UPDATE_SUCCESS',
  UPDATE_FAIL: 'PE_BROADCASTS_UPDATE_FAIL',
  SCHEDULE_BEGIN: 'PE_BROADCASTS_SCHEDULE_BEGIN',
  SCHEDULE_SUCCESS: 'PE_BROADCASTS_SCHEDULE_SUCCESS',
  SCHEDULE_FAIL: 'PE_BROADCASTS_SCHEDULE_FAIL',
  CANCEL_BEGIN: 'PE_BROADCASTS_CANCEL_BEGIN',
  CANCEL_SUCCESS: 'PE_BROADCASTS_CANCEL_SUCCESS',
  CANCEL_FAIL: 'PE_BROADCASTS_CANCEL_FAIL',
  PREVIEW_BEGIN: 'PE_BROADCASTS_PREVIEW_BEGIN',
  PREVIEW_SUCCESS: 'PE_BROADCASTS_PREVIEW_SUCCESS',
  PREVIEW_FAIL: 'PE_BROADCASTS_PREVIEW_FAIL',
  CLEAR_PREVIEW: 'PE_BROADCASTS_CLEAR_PREVIEW',
  METADATA_BEGIN: 'PE_BROADCASTS_METADATA_BEGIN',
  METADATA_SUCCESS: 'PE_BROADCASTS_METADATA_SUCCESS',
  METADATA_FAIL: 'PE_BROADCASTS_METADATA_FAIL',
};

export const PE_CONVERSATIONS = {
  FETCH_BEGIN: 'PE_CONVERSATIONS_FETCH_BEGIN',
  FETCH_SUCCESS: 'PE_CONVERSATIONS_FETCH_SUCCESS',
  FETCH_FAIL: 'PE_CONVERSATIONS_FETCH_FAIL',
  COUNTS_BEGIN: 'PE_CONVERSATIONS_COUNTS_BEGIN',
  COUNTS_SUCCESS: 'PE_CONVERSATIONS_COUNTS_SUCCESS',
  COUNTS_FAIL: 'PE_CONVERSATIONS_COUNTS_FAIL',
  FETCH_ONE_BEGIN: 'PE_CONVERSATIONS_FETCH_ONE_BEGIN',
  FETCH_ONE_SUCCESS: 'PE_CONVERSATIONS_FETCH_ONE_SUCCESS',
  FETCH_ONE_FAIL: 'PE_CONVERSATIONS_FETCH_ONE_FAIL',
  FETCH_EARLIER_BEGIN: 'PE_CONVERSATIONS_FETCH_EARLIER_BEGIN',
  FETCH_EARLIER_SUCCESS: 'PE_CONVERSATIONS_FETCH_EARLIER_SUCCESS',
  FETCH_EARLIER_FAIL: 'PE_CONVERSATIONS_FETCH_EARLIER_FAIL',
  CLEAR_CURRENT: 'PE_CONVERSATIONS_CLEAR_CURRENT',
  CREATE_NOTE_BEGIN: 'PE_CONVERSATIONS_CREATE_NOTE_BEGIN',
  CREATE_NOTE_SUCCESS: 'PE_CONVERSATIONS_CREATE_NOTE_SUCCESS',
  CREATE_NOTE_FAIL: 'PE_CONVERSATIONS_CREATE_NOTE_FAIL',
  CREATE_REPLY_BEGIN: 'PE_CONVERSATIONS_CREATE_REPLY_BEGIN',
  CREATE_REPLY_SUCCESS: 'PE_CONVERSATIONS_CREATE_REPLY_SUCCESS',
  CREATE_REPLY_FAIL: 'PE_CONVERSATIONS_CREATE_REPLY_FAIL',
  CREATE_SECURE_BEGIN: 'PE_CONVERSATIONS_CREATE_SECURE_BEGIN',
  CREATE_SECURE_SUCCESS: 'PE_CONVERSATIONS_CREATE_SECURE_SUCCESS',
  CREATE_SECURE_FAIL: 'PE_CONVERSATIONS_CREATE_SECURE_FAIL',
  SEND_PORTAL_LINK_BEGIN: 'PE_CONVERSATIONS_SEND_PORTAL_LINK_BEGIN',
  SEND_PORTAL_LINK_SUCCESS: 'PE_CONVERSATIONS_SEND_PORTAL_LINK_SUCCESS',
  SEND_PORTAL_LINK_FAIL: 'PE_CONVERSATIONS_SEND_PORTAL_LINK_FAIL',
};

// Clear errors action
export const CLEAR_PATIENT_ENGAGEMENT_ERRORS = 'CLEAR_PATIENT_ENGAGEMENT_ERRORS';

export const clearPatientEngagementErrors = () => (dispatch) => {
  dispatch({ type: CLEAR_PATIENT_ENGAGEMENT_ERRORS });
};

// ============ PHI PREFLIGHT (shared) ============

// Side-effect-free PHI check for the confirmation modals (manual reply, broadcast
// compose, automation-rule save). Resolves to the response body ({ phi_suspected })
// and REJECTS on any network/HTTP failure so callers fail safe by blocking
// (never silently send). Deliberately a thin direct-axios thunk rather than crud():
// crud() resolves with the full axios response and never rejects, but these callers
// need the parsed body AND a throw on failure. The regex lives on the backend only.
export const preflightPhi = (text) => async (dispatch) => {
  let response;
  try {
    response = await axios({
      method: 'POST',
      url: '/api/v1/patient_engagement/phi_preflight',
      // Named envelope, not a bare `text` key: it is on the always-filtered list
      // so the suspected-PHI string never reaches the request log or Sentry.
      data: { phi_preflight: { text } },
      // csrfToken() rather than the unguarded getElementsByName(...)[0].content
      // used elsewhere: a missing meta tag would throw a TypeError that reads as
      // a network failure to the caller.
      headers: { 'X-CSRF-TOKEN': csrfToken() },
    });
  } catch (error) {
    // Being outside crud() means the shared 401 handling does not apply. Without
    // this an expired session dead-ends the composer: the preflight fails, the
    // caller blocks the send and shows "please try again", and every retry fails
    // the same way with nothing telling the author to sign back in.
    if (error?.response?.status === 401) redirectToLogin(dispatch, error);
    throw error;
  }
  return response.data;
};

// ============ SETTINGS ============

export const fetchSettings = () => crud({
  dispatch: {
    begin: PE_SETTINGS.FETCH_BEGIN,
    fail: PE_SETTINGS.FETCH_FAIL,
    end: PE_SETTINGS.FETCH_SUCCESS,
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/settings',
});

// PATCH is the canonical update verb. telnyx_from_number and
// telnyx_messaging_profile_id are read-only server-side — callers must never
// include them in settingsData (the API 400s on unpermitted params).
export const updateSettings = (settingsData, onSuccess) => crud({
  dispatch: {
    begin: PE_SETTINGS.UPDATE_BEGIN,
    fail: PE_SETTINGS.UPDATE_FAIL,
    end: PE_SETTINGS.UPDATE_SUCCESS,
  },
  method: 'PATCH',
  url: '/api/v1/patient_engagement/settings',
  data: { patient_engagement_settings: settingsData },
  onSuccess,
});

// ============ AUTOMATION RULES ============

export const fetchAutomationRules = () => crud({
  dispatch: {
    begin: PE_AUTOMATION_RULES.FETCH_BEGIN,
    fail: PE_AUTOMATION_RULES.FETCH_FAIL,
    end: PE_AUTOMATION_RULES.FETCH_SUCCESS,
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/automation_rules',
});

export const fetchArchivedAutomationRules = () => crud({
  dispatch: {
    begin: PE_AUTOMATION_RULES.ARCHIVED_BEGIN,
    fail: PE_AUTOMATION_RULES.ARCHIVED_FAIL,
    end: PE_AUTOMATION_RULES.ARCHIVED_SUCCESS,
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/automation_rules/archived',
});

export const restoreAutomationRule = (id) => crud({
  dispatch: {
    begin: PE_AUTOMATION_RULES.UPDATE_BEGIN,
    fail: PE_AUTOMATION_RULES.UPDATE_FAIL,
    end: PE_AUTOMATION_RULES.RESTORE_SUCCESS,
  },
  method: 'POST',
  url: `/api/v1/patient_engagement/automation_rules/${id}/restore`,
});

// ENG-2938 (PHI-confirm): phi_acknowledged must ride at the TOP level of the
// request — the controller reads it from params[:phi_acknowledged] and it is NOT
// a permitted rule attribute (no such column), so nesting it inside
// patient_engagement_automation_rule trips strict strong-params. Lift it out of
// ruleData here so the editor can keep passing a single payload object.
export const createAutomationRule = (ruleData, onSuccess) => {
  const { phi_acknowledged: phiAcknowledged, ...rule } = ruleData;
  return crud({
    dispatch: {
      begin: PE_AUTOMATION_RULES.CREATE_BEGIN,
      fail: PE_AUTOMATION_RULES.CREATE_FAIL,
      end: PE_AUTOMATION_RULES.CREATE_SUCCESS,
    },
    method: 'POST',
    url: '/api/v1/patient_engagement/automation_rules',
    data: { patient_engagement_automation_rule: rule, ...(phiAcknowledged ? { phi_acknowledged: true } : {}) },
    onSuccess,
  });
};

export const updateAutomationRule = (id, ruleData, onSuccess) => {
  const { phi_acknowledged: phiAcknowledged, ...rule } = ruleData;
  return crud({
    dispatch: {
      begin: PE_AUTOMATION_RULES.UPDATE_BEGIN,
      fail: PE_AUTOMATION_RULES.UPDATE_FAIL,
      end: PE_AUTOMATION_RULES.UPDATE_SUCCESS,
    },
    method: 'PATCH',
    url: `/api/v1/patient_engagement/automation_rules/${id}`,
    data: { patient_engagement_automation_rule: rule, ...(phiAcknowledged ? { phi_acknowledged: true } : {}) },
    onSuccess,
  });
};

// A distinct endpoint prevents older servers interpreting archive as permanent deletion.
export const deleteAutomationRule = (id, onSuccess) => crud({
  dispatch: {
    begin: PE_AUTOMATION_RULES.DELETE_BEGIN,
    fail: PE_AUTOMATION_RULES.DELETE_FAIL,
    end: PE_AUTOMATION_RULES.DELETE_SUCCESS,
  },
  method: 'POST',
  url: `/api/v1/patient_engagement/automation_rules/${id}/archive`,
  onSuccess,
});

// Reorder all rules of one kind. ruleIds is the full ordered list for that
// kind; the API rewrites priority from list position (lower position wins).
export const reorderAutomationRules = (kind, ruleIds, onSuccess) => crud({
  dispatch: {
    begin: PE_AUTOMATION_RULES.REORDER_BEGIN,
    fail: PE_AUTOMATION_RULES.REORDER_FAIL,
    end: PE_AUTOMATION_RULES.REORDER_SUCCESS,
  },
  method: 'PATCH',
  url: '/api/v1/patient_engagement/automation_rules/reorder',
  data: { kind, rule_ids: ruleIds },
  onSuccess,
});

// Live preview for the rule editor (PR-2 §5.3). Callers must debounce; the
// optional AbortController signal lets a newer keystroke cancel the in-flight
// render so a stale response never overwrites a fresher one.
export const previewTemplate = (template, daysOut, signal) => crud({
  dispatch: {
    begin: PE_AUTOMATION_RULES.PREVIEW_BEGIN,
    fail: PE_AUTOMATION_RULES.PREVIEW_FAIL,
    end: PE_AUTOMATION_RULES.PREVIEW_SUCCESS,
  },
  method: 'POST',
  url: '/api/v1/patient_engagement/template_preview',
  data: { template, days_out: daysOut },
  signal,
});

// ============ BROADCASTS ============

export const fetchBroadcasts = (params) => crud({
  dispatch: {
    begin: PE_BROADCASTS.FETCH_BEGIN,
    fail: PE_BROADCASTS.FETCH_FAIL,
    end: PE_BROADCASTS.FETCH_SUCCESS,
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/broadcasts',
  params,
});

export const fetchBroadcast = (id) => crud({
  dispatch: {
    begin: PE_BROADCASTS.FETCH_ONE_BEGIN,
    fail: PE_BROADCASTS.FETCH_ONE_FAIL,
    end: PE_BROADCASTS.FETCH_ONE_SUCCESS,
  },
  method: 'GET',
  url: `/api/v1/patient_engagement/broadcasts/${id}`,
});

export const createBroadcast = (broadcastData, onSuccess) => crud({
  dispatch: {
    begin: PE_BROADCASTS.CREATE_BEGIN,
    fail: PE_BROADCASTS.CREATE_FAIL,
    end: PE_BROADCASTS.CREATE_SUCCESS,
  },
  method: 'POST',
  url: '/api/v1/patient_engagement/broadcasts',
  data: { patient_broadcast: broadcastData },
  onSuccess,
});

// opts.phiAcknowledged rides at the TOP level, beside the patient_broadcast
// envelope, because the controller reads params[:phi_acknowledged] and it is not
// a broadcast attribute. Only an edit to an already-SCHEDULED broadcast needs it
// (nothing re-gates a scheduled row before it fans out); a draft edit does not.
export const updateBroadcast = (id, broadcastData, opts, onSuccess) => crud({
  dispatch: {
    begin: PE_BROADCASTS.UPDATE_BEGIN,
    fail: PE_BROADCASTS.UPDATE_FAIL,
    end: PE_BROADCASTS.UPDATE_SUCCESS,
  },
  method: 'PATCH',
  url: `/api/v1/patient_engagement/broadcasts/${id}`,
  data: {
    patient_broadcast: broadcastData,
    ...(opts?.phiAcknowledged ? { phi_acknowledged: true } : {}),
  },
  onSuccess,
});

// scheduledFor: ISO-8601 datetime, or null/undefined for "send now" (the API
// stamps Time.current and the broadcast sits inert until PR 9's scheduler).
export const scheduleBroadcast = (id, scheduledFor, opts, onSuccess) => crud({
  dispatch: {
    begin: PE_BROADCASTS.SCHEDULE_BEGIN,
    fail: PE_BROADCASTS.SCHEDULE_FAIL,
    end: PE_BROADCASTS.SCHEDULE_SUCCESS,
  },
  method: 'PATCH',
  url: `/api/v1/patient_engagement/broadcasts/${id}/schedule`,
  data: { scheduled_for: scheduledFor || null, ...(opts?.phiAcknowledged ? { phi_acknowledged: true } : {}) },
  onSuccess,
});

export const cancelBroadcast = (id, onSuccess) => crud({
  dispatch: {
    begin: PE_BROADCASTS.CANCEL_BEGIN,
    fail: PE_BROADCASTS.CANCEL_FAIL,
    end: PE_BROADCASTS.CANCEL_SUCCESS,
  },
  method: 'PATCH',
  url: `/api/v1/patient_engagement/broadcasts/${id}/cancel`,
  onSuccess,
});

// Audience preview (count + 5-patient sample). Callers must debounce; the
// AbortController signal cancels superseded previews.
export const previewAudience = (audienceQuery, signal) => crud({
  dispatch: {
    begin: PE_BROADCASTS.PREVIEW_BEGIN,
    fail: PE_BROADCASTS.PREVIEW_FAIL,
    end: PE_BROADCASTS.PREVIEW_SUCCESS,
  },
  method: 'POST',
  url: '/api/v1/patient_engagement/broadcasts/preview',
  data: { audience_query: audienceQuery },
  signal,
});

export const clearAudiencePreview = () => (dispatch) => {
  dispatch({ type: PE_BROADCASTS.CLEAR_PREVIEW });
};

export const fetchAudienceMetadata = () => crud({
  dispatch: {
    begin: PE_BROADCASTS.METADATA_BEGIN,
    fail: PE_BROADCASTS.METADATA_FAIL,
    end: PE_BROADCASTS.METADATA_SUCCESS,
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/broadcasts/audience_metadata',
});

// ============ CONVERSATIONS ============

// The optional AbortController signal lets the inbox cancel a superseded list
// fetch — without it a slow response for an older filter/search can resolve
// last and clobber the newer list.
// A refresh (after a write, or a live update) keeps the current rows on screen.
export const fetchConversations = (params, signal, { refresh = false } = {}) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.FETCH_BEGIN,
    fail: PE_CONVERSATIONS.FETCH_FAIL,
    end: PE_CONVERSATIONS.FETCH_SUCCESS,
    ...(refresh ? { meta: { refresh: true } } : {}),
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/conversations',
  params,
  signal,
});

// Same abort discipline as fetchConversations: the inbox re-fetches counts with
// every list fetch, and a superseded counts response must never land over a
// newer one (crud returns { canceled: true } before any lifecycle action fires).
export const fetchConversationCounts = (signal) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.COUNTS_BEGIN,
    fail: PE_CONVERSATIONS.COUNTS_FAIL,
    end: PE_CONVERSATIONS.COUNTS_SUCCESS,
  },
  method: 'GET',
  url: '/api/v1/patient_engagement/conversations/counts',
  signal,
});

// The signal ties the thread fetch to the current selection: switching
// patients aborts the in-flight fetch so a slow response can never paint the
// previous patient's PHI over the newly selected thread.
// `refresh: true` re-reads an already-open thread after a write to it (a sent
// reply); the reducer then leaves the loading flag alone so the pane does not blink
// to a spinner over content that is about to be replaced by its fresher self.
export const fetchConversation = (id, params, signal, { refresh = false } = {}) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.FETCH_ONE_BEGIN,
    fail: PE_CONVERSATIONS.FETCH_ONE_FAIL,
    end: PE_CONVERSATIONS.FETCH_ONE_SUCCESS,
    ...(refresh ? { meta: { refresh: true } } : {}),
  },
  method: 'GET',
  url: `/api/v1/patient_engagement/conversations/${id}`,
  params,
  signal,
});

// Walks the timeline back one page; the reducer prepends the returned items.
// Carries the same selection signal as fetchConversation so a stale earlier
// page can't prepend into a different conversation's timeline.
export const fetchEarlierTimeline = (id, params, signal) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.FETCH_EARLIER_BEGIN,
    fail: PE_CONVERSATIONS.FETCH_EARLIER_FAIL,
    end: PE_CONVERSATIONS.FETCH_EARLIER_SUCCESS,
  },
  method: 'GET',
  url: `/api/v1/patient_engagement/conversations/${id}`,
  params,
  signal,
});

export const clearCurrentConversation = () => (dispatch) => {
  dispatch({ type: PE_CONVERSATIONS.CLEAR_CURRENT });
};

// Carries `meta.conversationId` so the reducer can drop a response that no longer
// belongs to the selected thread — otherwise a slow POST for patient A resolves
// after the staff member has clicked patient B and appends A's clinical note to
// B's timeline (and shows A's saving/error state in B's composer).
//
// Deliberately NOT abort-signalled, unlike the conversation fetches: a note is a
// WRITE. Aborting the request client-side does not stop the server committing it,
// so it would leave a persisted note the author believes was discarded. The note
// always finishes and is always saved against `conversationId`; only its effect on
// the currently-rendered thread is scoped.
export const createConversationNote = (conversationId, body, onSuccess) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.CREATE_NOTE_BEGIN,
    fail: PE_CONVERSATIONS.CREATE_NOTE_FAIL,
    end: PE_CONVERSATIONS.CREATE_NOTE_SUCCESS,
    meta: { conversationId: Number(conversationId) },
  },
  method: 'POST',
  url: `/api/v1/patient_engagement/conversations/${conversationId}/notes`,
  data: { patient_conversation_note: { body } },
  onSuccess,
});

// Queues a manual outbound SMS reply (PR 7). The backend returns the queued
// message, which the reducer optimistically appends to the timeline (no refetch,
// to avoid racing the async reconcile worker). The delivery badge and any PHI
// lock reconcile the next time the thread is opened.
//
// Carries `meta.conversationId` for the same reason createConversationNote does,
// and it matters more here: this message was actually TRANSMITTED to a patient. A
// slow POST for patient A resolving after the staff member clicked patient B would
// render A's outbound SMS under B's name and MRN, telling the sender they just
// texted the wrong person.
//
// Deliberately NOT abort-signalled. A reply is a WRITE that hands a real text
// message to the carrier; aborting client-side does not unsend it, so it would
// leave a patient texted while the author believes the send was cancelled. The
// reply always completes and is always sent against `conversationId`; only its
// effect on the currently-rendered thread is scoped.
export const createConversationReply = (conversationId, body, opts, onSuccess) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.CREATE_REPLY_BEGIN,
    fail: PE_CONVERSATIONS.CREATE_REPLY_FAIL,
    end: PE_CONVERSATIONS.CREATE_REPLY_SUCCESS,
    meta: { conversationId: Number(conversationId) },
  },
  method: 'POST',
  url: `/api/v1/patient_engagement/conversations/${conversationId}/reply`,
  data: { patient_message: { body, ...(opts?.phiAcknowledged ? { phi_acknowledged: true } : {}) } },
  onSuccess,
});

// Secure chat (ENG-3884): the body stays in the patient portal and the patient
// is texted a link. The response carries both rows (the secure message and the
// link text), which the reducer appends. Scoped and never abort-signalled for
// the same reasons as createConversationReply, and never replayed on a 503: the
// server may already have texted the patient.
// Photos and PDFs go as multipart, inside the patient_message envelope (the
// server filters that whole envelope from its logs).
const secureMessageData = (body, files) => {
  if (!files || files.length === 0) return { patient_message: { body } };
  const data = new FormData();
  data.append('patient_message[body]', body);
  files.forEach((file) => data.append('patient_message[files][]', file));
  return data;
};

export const createSecureMessage = (conversationId, body, files, onSuccess) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.CREATE_SECURE_BEGIN,
    fail: PE_CONVERSATIONS.CREATE_SECURE_FAIL,
    end: PE_CONVERSATIONS.CREATE_SECURE_SUCCESS,
    meta: { conversationId: Number(conversationId) },
  },
  method: 'POST',
  url: `/api/v1/patient_engagement/conversations/${conversationId}/secure_messages`,
  data: secureMessageData(body, files),
  retryOnUnavailable: false,
  onSuccess,
});

// Unlocks a locked portal (five wrong dates of birth) and texts a fresh link;
// also resends an expired one.
export const sendPortalLink = (conversationId, onSuccess) => crud({
  dispatch: {
    begin: PE_CONVERSATIONS.SEND_PORTAL_LINK_BEGIN,
    fail: PE_CONVERSATIONS.SEND_PORTAL_LINK_FAIL,
    end: PE_CONVERSATIONS.SEND_PORTAL_LINK_SUCCESS,
    meta: { conversationId: Number(conversationId) },
  },
  method: 'POST',
  url: `/api/v1/patient_engagement/conversations/${conversationId}/portal_link`,
  retryOnUnavailable: false,
  onSuccess,
});
